export type SharePayloadErrorCode = "too-large" | "malformed" | "unsupported";

export class SharePayloadError extends Error {
  override readonly name = "SharePayloadError";
  override readonly cause: unknown;

  constructor(
    readonly code: SharePayloadErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.cause = cause;
  }
}

export function isSharePayloadError(
  error: unknown,
  code?: SharePayloadErrorCode,
): error is SharePayloadError {
  return (
    error instanceof SharePayloadError &&
    (code === undefined || error.code === code)
  );
}

export const MAX_SHARE_FRAGMENT_LENGTH = 32_768;
const MAX_DECODED_BYTES = 262_144;

export async function encodeSharePayload(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  if (bytes.byteLength > MAX_DECODED_BYTES) throw payloadTooLargeError();
  const plain = `1j${toBase64Url(bytes)}`;
  if (typeof CompressionStream === "undefined") return assertFits(plain);

  const compressed = await transformBytes(
    bytes,
    new CompressionStream("gzip"),
    MAX_DECODED_BYTES,
  );
  const gzip = `1g${toBase64Url(compressed)}`;
  return assertFits(gzip.length < plain.length ? gzip : plain);
}

export async function decodeSharePayload(fragment: string): Promise<unknown> {
  if (fragment.length > MAX_SHARE_FRAGMENT_LENGTH) throw payloadTooLargeError();
  if (!fragment.startsWith("1j") && !fragment.startsWith("1g"))
    throw new SharePayloadError("unsupported", "Unsupported share payload");

  const source = fromBase64Url(fragment.slice(2));
  const bytes = fragment[1] === "g" ? await decompressBounded(source) : source;

  try {
    return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch (error) {
    throw malformedPayloadError(error);
  }
}

function assertFits(payload: string): string {
  if (payload.length > MAX_SHARE_FRAGMENT_LENGTH) throw payloadTooLargeError();
  return payload;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize),
    );
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

function fromBase64Url(value: string): Uint8Array {
  try {
    if (!/^[A-Za-z0-9_-]*$/.test(value) || value.length % 4 === 1)
      throw new Error("Invalid Base64URL encoding");
    const padded = value.padEnd(Math.ceil(value.length / 4) * 4, "=");
    const binary = atob(padded.replaceAll("-", "+").replaceAll("_", "/"));
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    if (toBase64Url(bytes) !== value)
      throw new Error("Non-canonical Base64URL encoding");
    return bytes;
  } catch (error) {
    throw malformedPayloadError(error);
  }
}

async function transformBytes(
  bytes: Uint8Array,
  transform: CompressionStream | DecompressionStream,
  limit: number,
): Promise<Uint8Array> {
  const writer = transform.writable.getWriter();
  const writing = writer
    .write(new Uint8Array(bytes))
    .then(() => writer.close());
  const [result] = await Promise.all([
    readBounded(transform.readable, limit),
    writing,
  ]);
  return result;
}

async function decompressBounded(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === "undefined")
    throw new SharePayloadError(
      "unsupported",
      "Compressed share payload is unsupported",
    );
  try {
    return await transformBytes(
      bytes,
      new DecompressionStream("gzip"),
      MAX_DECODED_BYTES,
    );
  } catch (error) {
    if (isSharePayloadError(error, "too-large")) throw error;
    throw malformedPayloadError(error);
  }
}

async function readBounded(
  stream: ReadableStream<Uint8Array>,
  limit: number,
): Promise<Uint8Array> {
  const reader = stream.getReader();
  let chunks: Uint8Array[];
  let size: number;
  try {
    ({ chunks, size } = await readChunks(reader, limit));
  } catch (error) {
    if (isSharePayloadError(error, "too-large")) throw error;
    throw malformedPayloadError(error);
  }
  const output = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return output;
}

async function readChunks(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  limit: number,
): Promise<{ chunks: Uint8Array[]; size: number }> {
  const chunks: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    size += value.byteLength;
    if (size > limit) {
      await reader.cancel();
      throw payloadTooLargeError();
    }
    chunks.push(value);
  }
  return { chunks, size };
}

function payloadTooLargeError(): SharePayloadError {
  return new SharePayloadError("too-large", "Share payload is too large");
}

function malformedPayloadError(cause?: unknown): SharePayloadError {
  return new SharePayloadError("malformed", "Malformed share payload", cause);
}
